import * as THREE from 'three';
import { Noise } from './Noise.js';
import { SeededRandom } from '../utils/random.js';
import { FLORA, PLANET } from '../utils/constants.js';

/* ── Per-biome tree style definitions ────────────────────────── */

const TREE_STYLES = {
  forest: {
    types: ['broadleaf', 'fungus', 'fern'],
    trunkColor:  [0.10, 0.30, 0.22],
    canopyColor: [0.30, 0.65, 0.35],
    glowColor:   null,
    density: 1.0,
  },
  ocean: {
    types: ['coral', 'kelp'],
    trunkColor:  [0.55, 0.40, 0.30],
    canopyColor: [0.52, 0.60, 0.40],
    glowColor:   null,
    density: 0.7,
  },
  ice: {
    types: ['crystal_tree', 'frozen_spire'],
    trunkColor:  [0.58, 0.15, 0.70],
    canopyColor: [0.56, 0.30, 0.80],
    glowColor:   [0.56, 0.50, 0.60],
    density: 0.55,
  },
  crystal: {
    types: ['crystal_tree', 'spire_cluster'],
    trunkColor:  [0.78, 0.40, 0.40],
    canopyColor: [0.80, 0.65, 0.60],
    glowColor:   [0.80, 0.55, 0.45],
    density: 0.8,
  },
  lava: {
    types: ['charred', 'ember_stalk'],
    trunkColor:  [0.02, 0.15, 0.10],
    canopyColor: [0.04, 0.80, 0.30],
    glowColor:   [0.03, 0.90, 0.40],
    density: 0.45,
  },
  desert: {
    types: ['cactus', 'dead_tree', 'succulent'],
    trunkColor:  [0.08, 0.45, 0.35],
    canopyColor: [0.25, 0.55, 0.42],
    glowColor:   null,
    density: 0.55,
  },
  rocky: {
    types: ['scrub', 'dead_tree'],
    trunkColor:  [0.06, 0.20, 0.28],
    canopyColor: [0.12, 0.35, 0.30],
    glowColor:   null,
    density: 0.5,
  },
};

/**
 * Procedurally generates alien trees and flora meshes on planet surfaces.
 * All generation is deterministic via SeededRandom for multiplayer consistency.
 *
 * Produces a serialisable data array (for future network sync) and
 * a THREE.Group of instanced meshes.
 */
export class TreeGenerator {
  constructor() {
    /** @type {{ id: string, type: string, position: {x,y,z}, direction: {x,y,z}, scale: number, seed: number }[]} */
    this.trees = [];
    this.group = new THREE.Group();
    this.group.name = 'trees';
  }

  /* ── public API ────────────────────────────────── */

  /**
   * Generate trees for a planet.
   * @param {object}   planetData   – planet config (seed, type, size, terrain)
   * @param {number}   planetRadius – SURFACE_RADIUS × size
   * @param {function} getHeightFn  – (normalizedDir) => surfaceHeight
   * @param {object}   [plan]       – shared SurfacePlan; trees stay out of
   *                                  every reserved zone (clearings, rock
   *                                  outcrops, crystal fields, basins)
   * @returns {{ group: THREE.Group, trees: object[] }}
   */
  generate(planetData, planetRadius, getHeightFn, plan = null) {
    this.clear();

    const style = TREE_STYLES[planetData.type];
    if (!style || planetData.type === 'gas') {
      return { group: this.group, trees: this.trees };
    }

    const rng   = new SeededRandom(planetData.seed + 11111);
    const noise = new Noise(planetData.seed + 22222);

    const areaFactor = Math.min(3, Math.max(0.5, (planetRadius / PLANET.DENSITY_REF_RADIUS) ** 2));
    const baseCount = Math.round(
      (FLORA.MIN_TREES + rng.next() * (FLORA.MAX_TREES - FLORA.MIN_TREES)) * style.density * areaFactor,
    );
    const minAngle = FLORA.TREE_MIN_SPACING / planetRadius;

    const placedDirs = [];
    let placed = 0;
    let attempts = 0;

    while (placed < baseCount && attempts < baseCount * 12) {
      attempts++;

      // Random direction on unit sphere
      const u     = rng.next();
      const v     = rng.next();
      const theta = Math.acos(2 * u - 1);
      const phi   = 2 * Math.PI * v;
      const dir   = new THREE.Vector3(
        Math.sin(theta) * Math.cos(phi),
        Math.cos(theta),
        Math.sin(theta) * Math.sin(phi),
      ).normalize();

      // Noise-based density mask — threshold high enough to carve real
      // clearings and groves; uniform scatter is the giveaway of
      // procedural placement.
      const nVal = noise.fbm(dir.x * 4, dir.y * 4, dir.z * 4, 3) * 0.5 + 0.5;
      if (nVal < 0.45) continue; // sparse zone → skip

      // Stay out of reserved zones — groves grow in the open land between
      // clearings, rock outcrops, crystal fields, and thermal basins.
      if (plan && !plan.isFree(dir, 2.5)) continue;

      // Spacing check
      let tooClose = false;
      for (const d of placedDirs) {
        if (dir.angleTo(d) < minAngle) { tooClose = true; break; }
      }
      if (tooClose) continue;
      placedDirs.push(dir.clone());

      const surfaceHeight = getHeightFn(dir);
      const treeSeed = planetData.seed + placed * 173 + 7;
      const treeRng = new SeededRandom(treeSeed);
      const treeType = treeRng.pick(style.types);
      const scale = FLORA.TREE_MIN_SCALE + treeRng.next() * (FLORA.TREE_MAX_SCALE - FLORA.TREE_MIN_SCALE);

      // Data record (serialisable for multiplayer)
      const treeData = {
        id: `tree_${planetData.seed}_${placed}`,
        type: treeType,
        position: { x: dir.x * surfaceHeight, y: dir.y * surfaceHeight, z: dir.z * surfaceHeight },
        direction: { x: dir.x, y: dir.y, z: dir.z },
        scale,
        seed: treeSeed,
        planetType: planetData.type,
      };
      this.trees.push(treeData);

      // Build mesh
      const mesh = this._createTreeMesh(treeData, style);
      this.group.add(mesh);

      placed++;
    }

    return { group: this.group, trees: this.trees };
  }

  clear() {
    this.group.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
        else child.material.dispose();
      }
    });
    while (this.group.children.length) this.group.remove(this.group.children[0]);
    this.trees = [];
  }

  /* ── mesh builders ─────────────────────────────── */

  _createTreeMesh(data, style) {
    const group = new THREE.Group();
    const rng = new SeededRandom(data.seed);
    const dir = new THREE.Vector3(data.direction.x, data.direction.y, data.direction.z);
    const pos = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
    const s = data.scale;

    switch (data.type) {
      case 'broadleaf':  this._buildBroadleaf(group, s, rng, style); break;
      case 'fungus':     this._buildFungus(group, s, rng, style); break;
      case 'fern':       this._buildFern(group, s, rng, style); break;
      case 'coral':      this._buildCoral(group, s, rng, style); break;
      case 'kelp':       this._buildKelp(group, s, rng, style); break;
      case 'crystal_tree': this._buildCrystalTree(group, s, rng, style); break;
      case 'frozen_spire': this._buildFrozenSpire(group, s, rng, style); break;
      case 'spire_cluster': this._buildSpireCluster(group, s, rng, style); break;
      case 'charred':    this._buildCharred(group, s, rng, style); break;
      case 'ember_stalk': this._buildEmberStalk(group, s, rng, style); break;
      case 'cactus':     this._buildCactus(group, s, rng, style); break;
      case 'dead_tree':  this._buildDeadTree(group, s, rng, style); break;
      case 'succulent':  this._buildSucculent(group, s, rng, style); break;
      case 'scrub':      this._buildScrub(group, s, rng, style); break;
      default:           this._buildBroadleaf(group, s, rng, style); break;
    }

    // Position & orient outward from planet center
    group.position.copy(pos);
    const up = dir.clone();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
    group.quaternion.copy(quat);

    // Random spin around up axis
    group.rotateOnAxis(new THREE.Vector3(0, 1, 0), rng.next() * Math.PI * 2);

    group.name = `tree-${data.id}`;
    group.userData.treeData = data;
    return group;
  }

  /* ── individual tree type builders ─────────────── */

  _makeTrunkMat(style) {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...style.trunkColor),
      roughness: 0.88,
      metalness: 0.02,
    });
  }

  _makeCanopyMat(style, opacity = 1) {
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...style.canopyColor),
      roughness: 0.7,
      metalness: 0.0,
    });
    if (opacity < 1) {
      mat.transparent = true;
      mat.opacity = opacity;
      mat.depthWrite = false;
    }
    if (style.glowColor) {
      mat.emissive = new THREE.Color().setHSL(...style.glowColor);
      mat.emissiveIntensity = 0.15;
    }
    return mat;
  }

  _buildBroadleaf(group, s, rng, style) {
    // Trunk
    const trunkH = s * rng.range(2.5, 4.0);
    const trunkR = s * rng.range(0.15, 0.25);
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(trunkR * 0.6, trunkR, trunkH, 6),
      this._makeTrunkMat(style),
    );
    trunk.position.y = trunkH / 2;
    trunk.castShadow = true;
    group.add(trunk);

    // Canopy — 2-3 leaf clusters
    const canopyMat = this._makeCanopyMat(style, 0.9);
    const clusters = rng.int(2, 3);
    for (let i = 0; i < clusters; i++) {
      const r = s * rng.range(0.8, 1.6);
      const geo = new THREE.IcosahedronGeometry(r, 1);
      // Squash slightly
      geo.scale(1, rng.range(0.5, 0.8), 1);
      const leaf = new THREE.Mesh(geo, canopyMat);
      leaf.position.set(
        rng.range(-0.4, 0.4) * s,
        trunkH + r * rng.range(0.2, 0.5),
        rng.range(-0.4, 0.4) * s,
      );
      leaf.castShadow = true;
      group.add(leaf);
    }
  }

  _buildFungus(group, s, rng, style) {
    // Stalk
    const stalkH = s * rng.range(1.5, 3.0);
    const stalkR = s * rng.range(0.12, 0.22);
    const stalk = new THREE.Mesh(
      new THREE.CylinderGeometry(stalkR * 0.8, stalkR * 1.2, stalkH, 6),
      this._makeTrunkMat(style),
    );
    stalk.position.y = stalkH / 2;
    group.add(stalk);

    // Cap — wide flat sphere
    const capR = s * rng.range(1.0, 2.0);
    const capGeo = new THREE.SphereGeometry(capR, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.55);
    const capMat = this._makeCanopyMat(style, 0.85);
    const cap = new THREE.Mesh(capGeo, capMat);
    cap.position.y = stalkH;
    cap.castShadow = true;
    group.add(cap);

    // Spots on cap
    const spotMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(style.canopyColor[0] + 0.05, 0.8, 0.7),
      emissive: new THREE.Color().setHSL(style.canopyColor[0] + 0.05, 0.6, 0.4),
      emissiveIntensity: 0.3,
    });
    const spots = rng.int(3, 6);
    for (let i = 0; i < spots; i++) {
      const spotGeo = new THREE.SphereGeometry(s * 0.08, 4, 4);
      const spot = new THREE.Mesh(spotGeo, spotMat);
      const angle = rng.next() * Math.PI * 2;
      const rr = rng.range(0.3, 0.8) * capR;
      spot.position.set(
        Math.cos(angle) * rr,
        stalkH + capR * 0.3,
        Math.sin(angle) * rr,
      );
      group.add(spot);
    }
  }

  _buildFern(group, s, rng, style) {
    const canopyMat = this._makeCanopyMat(style, 0.85);
    const fronds = rng.int(5, 8);
    for (let i = 0; i < fronds; i++) {
      const angle = (i / fronds) * Math.PI * 2;
      const length = s * rng.range(1.0, 2.0);
      const geo = new THREE.ConeGeometry(s * 0.3, length, 4);
      const frond = new THREE.Mesh(geo, canopyMat);
      frond.position.set(
        Math.cos(angle) * s * 0.3,
        length * 0.4,
        Math.sin(angle) * s * 0.3,
      );
      frond.rotation.z = Math.cos(angle) * 0.5;
      frond.rotation.x = Math.sin(angle) * 0.5;
      frond.castShadow = true;
      group.add(frond);
    }
  }

  _buildCoral(group, s, rng, style) {
    const branches = rng.int(4, 8);
    const mat = this._makeCanopyMat(style, 0.9);
    for (let i = 0; i < branches; i++) {
      const h = s * rng.range(1.0, 2.5);
      const r = s * rng.range(0.08, 0.18);
      const geo = new THREE.CylinderGeometry(r * 0.3, r, h, 5);
      const branch = new THREE.Mesh(geo, mat);
      const angle = rng.next() * Math.PI * 2;
      const spread = rng.range(0.0, 0.6) * s;
      branch.position.set(Math.cos(angle) * spread, h / 2, Math.sin(angle) * spread);
      branch.rotation.set(rng.range(-0.3, 0.3), 0, rng.range(-0.3, 0.3));
      branch.castShadow = true;
      group.add(branch);

      // Tip bulb
      const bulb = new THREE.Mesh(
        new THREE.SphereGeometry(r * 2, 6, 6),
        mat,
      );
      bulb.position.set(branch.position.x, h, branch.position.z);
      group.add(bulb);
    }
  }

  _buildKelp(group, s, rng, style) {
    const stalks = rng.int(3, 6);
    const mat = this._makeCanopyMat(style, 0.75);
    for (let i = 0; i < stalks; i++) {
      const h = s * rng.range(2.0, 4.0);
      const r = s * 0.06;
      const geo = new THREE.CylinderGeometry(r * 0.5, r, h, 4);
      const stalk = new THREE.Mesh(geo, mat);
      const angle = rng.next() * Math.PI * 2;
      const spread = rng.range(0.0, 0.4) * s;
      stalk.position.set(Math.cos(angle) * spread, h / 2, Math.sin(angle) * spread);
      stalk.rotation.set(rng.range(-0.15, 0.15), 0, rng.range(-0.15, 0.15));
      group.add(stalk);
    }
  }

  _buildCrystalTree(group, s, rng, style) {
    // Central trunk crystal
    const trunkH = s * rng.range(2.5, 4.5);
    const trunkR = s * rng.range(0.2, 0.35);
    const mat = this._makeCanopyMat(style, 0.8);
    mat.roughness = 0.1;
    mat.metalness = 0.4;
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(trunkR * 0.1, trunkR, trunkH, 5),
      mat,
    );
    trunk.position.y = trunkH / 2;
    trunk.castShadow = true;
    group.add(trunk);

    // Branch crystals
    const branches = rng.int(3, 6);
    for (let i = 0; i < branches; i++) {
      const bH = s * rng.range(0.8, 2.0);
      const bR = s * rng.range(0.08, 0.15);
      const branch = new THREE.Mesh(
        new THREE.CylinderGeometry(bR * 0.1, bR, bH, 4),
        mat,
      );
      const angle = rng.next() * Math.PI * 2;
      const yOff = rng.range(0.3, 0.8) * trunkH;
      branch.position.set(
        Math.cos(angle) * trunkR * 2,
        yOff,
        Math.sin(angle) * trunkR * 2,
      );
      branch.rotation.set(
        rng.range(-0.6, 0.6),
        angle,
        rng.range(-0.3, 0.3),
      );
      group.add(branch);
    }
  }

  _buildFrozenSpire(group, s, rng, style) {
    const h = s * rng.range(3.0, 5.0);
    const r = s * rng.range(0.25, 0.5);
    const mat = this._makeCanopyMat(style, 0.7);
    mat.roughness = 0.05;
    mat.metalness = 0.3;
    const spire = new THREE.Mesh(
      new THREE.ConeGeometry(r, h, 5),
      mat,
    );
    spire.position.y = h / 2;
    spire.castShadow = true;
    group.add(spire);
  }

  _buildSpireCluster(group, s, rng, style) {
    const count = rng.int(3, 7);
    const mat = this._makeCanopyMat(style, 0.75);
    mat.roughness = 0.08;
    mat.metalness = 0.45;
    for (let i = 0; i < count; i++) {
      const h = s * rng.range(1.0, 3.5);
      const r = s * rng.range(0.1, 0.25);
      const spire = new THREE.Mesh(
        new THREE.ConeGeometry(r, h, rng.int(4, 6)),
        mat,
      );
      const angle = rng.next() * Math.PI * 2;
      const spread = rng.range(0.0, 0.6) * s;
      spire.position.set(
        Math.cos(angle) * spread,
        h / 2,
        Math.sin(angle) * spread,
      );
      spire.rotation.set(rng.range(-0.15, 0.15), 0, rng.range(-0.15, 0.15));
      spire.castShadow = true;
      group.add(spire);
    }
  }

  _buildCharred(group, s, rng, style) {
    const trunkH = s * rng.range(2.0, 3.5);
    const trunkR = s * rng.range(0.15, 0.25);
    const mat = this._makeTrunkMat(style);
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(trunkR * 0.4, trunkR, trunkH, 5),
      mat,
    );
    trunk.position.y = trunkH / 2;
    trunk.castShadow = true;
    group.add(trunk);

    // Charred broken branches
    const branches = rng.int(2, 4);
    for (let i = 0; i < branches; i++) {
      const bH = s * rng.range(0.5, 1.2);
      const branch = new THREE.Mesh(
        new THREE.CylinderGeometry(s * 0.02, s * 0.06, bH, 3),
        mat,
      );
      const y = rng.range(0.4, 0.9) * trunkH;
      const angle = rng.next() * Math.PI * 2;
      branch.position.set(Math.cos(angle) * trunkR, y, Math.sin(angle) * trunkR);
      branch.rotation.set(rng.range(-0.5, 0.5), angle, rng.range(0.3, 0.8));
      group.add(branch);
    }
  }

  _buildEmberStalk(group, s, rng, style) {
    const h = s * rng.range(1.5, 3.0);
    const r = s * rng.range(0.08, 0.15);
    const stalkMat = this._makeTrunkMat(style);
    const stalk = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 0.5, r, h, 5),
      stalkMat,
    );
    stalk.position.y = h / 2;
    group.add(stalk);

    // Glowing tip
    const tipMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(0.04, 0.9, 0.5),
      emissive: new THREE.Color().setHSL(0.03, 1.0, 0.4),
      emissiveIntensity: 1.1,
    });
    const tip = new THREE.Mesh(
      new THREE.SphereGeometry(s * 0.2, 6, 6),
      tipMat,
    );
    tip.position.y = h + s * 0.1;
    group.add(tip);
  }

  _buildCactus(group, s, rng, style) {
    const trunkH = s * rng.range(1.5, 3.0);
    const trunkR = s * rng.range(0.2, 0.3);
    const mat = this._makeCanopyMat(style);
    const trunk = new THREE.Mesh(
      new THREE.CapsuleGeometry(trunkR, trunkH, 6, 8),
      mat,
    );
    trunk.position.y = trunkH / 2 + trunkR;
    trunk.castShadow = true;
    group.add(trunk);

    // Arms
    const arms = rng.int(1, 3);
    for (let i = 0; i < arms; i++) {
      const armH = s * rng.range(0.6, 1.4);
      const armR = trunkR * 0.6;
      const arm = new THREE.Mesh(
        new THREE.CapsuleGeometry(armR, armH, 4, 6),
        mat,
      );
      const y = rng.range(0.4, 0.7) * trunkH;
      const side = i % 2 === 0 ? 1 : -1;
      arm.position.set(side * trunkR * 1.5, y + armH * 0.5, 0);
      arm.rotation.z = side * rng.range(0.3, 0.8);
      arm.castShadow = true;
      group.add(arm);
    }
  }

  _buildDeadTree(group, s, rng, style) {
    const trunkH = s * rng.range(2.0, 3.5);
    const trunkR = s * rng.range(0.12, 0.22);
    const mat = this._makeTrunkMat(style);
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(trunkR * 0.3, trunkR, trunkH, 5),
      mat,
    );
    trunk.position.y = trunkH / 2;
    trunk.castShadow = true;
    group.add(trunk);

    // Bare branches
    const branches = rng.int(3, 6);
    for (let i = 0; i < branches; i++) {
      const bLen = s * rng.range(0.5, 1.5);
      const branch = new THREE.Mesh(
        new THREE.CylinderGeometry(s * 0.01, s * 0.04, bLen, 3),
        mat,
      );
      const y = rng.range(0.5, 0.95) * trunkH;
      const angle = rng.next() * Math.PI * 2;
      branch.position.set(
        Math.cos(angle) * trunkR,
        y,
        Math.sin(angle) * trunkR,
      );
      branch.rotation.set(rng.range(-0.4, 0.4), angle, rng.range(0.4, 1.2));
      group.add(branch);
    }
  }

  _buildSucculent(group, s, rng, style) {
    const leaves = rng.int(5, 9);
    const mat = this._makeCanopyMat(style);
    for (let i = 0; i < leaves; i++) {
      const angle = (i / leaves) * Math.PI * 2;
      const geo = new THREE.SphereGeometry(s * rng.range(0.3, 0.5), 6, 5);
      geo.scale(1, 0.5, 1.5);
      const leaf = new THREE.Mesh(geo, mat);
      leaf.position.set(
        Math.cos(angle) * s * 0.25,
        s * 0.2,
        Math.sin(angle) * s * 0.25,
      );
      leaf.rotation.y = angle;
      leaf.rotation.x = -0.3;
      leaf.castShadow = true;
      group.add(leaf);
    }
  }

  _buildScrub(group, s, rng, style) {
    const clumps = rng.int(3, 6);
    const mat = this._makeCanopyMat(style, 0.85);
    for (let i = 0; i < clumps; i++) {
      const r = s * rng.range(0.3, 0.7);
      const geo = new THREE.IcosahedronGeometry(r, 0);
      geo.scale(1, rng.range(0.4, 0.7), 1);
      const clump = new THREE.Mesh(geo, mat);
      const angle = rng.next() * Math.PI * 2;
      const spread = rng.range(0, 0.5) * s;
      clump.position.set(
        Math.cos(angle) * spread,
        r * 0.3,
        Math.sin(angle) * spread,
      );
      clump.castShadow = true;
      group.add(clump);
    }
  }
}
